import React, { type ComponentProps, useRef } from 'react';
import { StyleSheet, View, type ViewInstance } from 'react-native';

import { theme } from '@garnish/constants';
import { Button, useHovered, webOnlyStyles } from '@garnish/core';

// ─────────────────────────────────────────────────────────────────────────────

export const PartnerLineItemFloatingButton = (props: Props) => {
  const { hoveredStyle, style, ...buttonProps } = props;

  // ─── Refs ────────────────────────────────────────────────────────────

  const buttonRef = useRef<ViewInstance>(null);

  // ─── Derived ─────────────────────────────────────────────────────────

  const isHovered = useHovered(buttonRef);

  // ─── Styles ──────────────────────────────────────────────────────────

  const buttonStyles = [styles.button, style, isHovered ? hoveredStyle : undefined];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      <Button
        ref={buttonRef}
        {...buttonProps}
        size="large-wide"
        maxFontSizeMultiplier={LABEL_MAX_FONT_SIZE_MULTIPLIER}
        style={buttonStyles}
        width="100%"
      />
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const LABEL_MAX_FONT_SIZE_MULTIPLIER = 1.1;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  button: {
    paddingHorizontal: theme.spacing['4'],
    boxShadow: '0px 8px 40px 4px rgba(14, 21, 14, 0.09)',
    ...webOnlyStyles({
      transition: `background-color ${theme.transitions.base}ms, border-color ${theme.transitions.base}ms, box-shadow ${theme.transitions.base}ms, color ${theme.transitions.base}ms`,
    }),
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = Omit<ComponentProps<typeof Button>, 'size' | 'width'> & {
  hoveredStyle?: ComponentProps<typeof Button>['style'];
};
